import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import SiteNav from "../components/SiteNav";
import SiteFooter from "../components/SiteFooter";
import { ArrowLeft, Play, Square, Footprints, Smartphone, Flame } from "lucide-react";

/* /walk — senior-friendly step counter that lives in a regular web browser
   but can be saved to the phone's home screen so it feels like an app.

   ZERO setup. No login. No fitness-tracker pairing. One green START button.
   The phone counts steps via the browser's DeviceMotion API (built into
   every iPhone & Android Chrome). Storage is keyed by a per-browser
   device_id we keep in localStorage so a streak persists for the same
   phone without ever asking for an email.

   Honest about limits: motion sensing only runs while the screen is on
   and the browser tab is in front. We tell the user this in plain English. */

const G    = "#2E9F3A";
const O    = "#F39A1F";
const WINE = "#0F172A";

// ─── Step-counting tuning ───
// Average human stride: 2.5 ft. 5,280 ft/mi ÷ 2.5 = 2,112 steps/mile.
const STEPS_PER_MILE = 2112;
// Calorie burn ≈ 0.04 kcal/step (rough; ~80 kcal/2000 steps for an avg adult).
const CAL_PER_STEP = 0.04;

// Threshold above 1g (9.81) we treat as a "step impulse". Anything between
// 1.15 and 2.5 fires; the cooldown prevents one step from triggering twice.
const STEP_THRESHOLD = 2.4;        // raised from 1.15 — was over-counting 4x (Mike walked 40 steps, app logged 160)
const STEP_COOLDOWN_MS = 380;      // raised from 280 — enforces realistic ~2.5 step/sec max walking cadence

function getOrCreateDeviceId() {
  const KEY = "fa.walk.device_id";
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = "dev_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(KEY, id);
  }
  return id;
}

export default function WalkPage({ embedded = false }) {
  const nav = useNavigate();
  const loc = useLocation();
  const [today, setToday] = useState({ steps: 0, streak_days: 0, duration_sec: 0 });
  const [verse, setVerse] = useState(null);
  const [walking, setWalking] = useState(false);
  const [sessionSteps, setSessionSteps] = useState(0);
  const [sessionSecs, setSessionSecs] = useState(0);
  const [needsPermission, setNeedsPermission] = useState(false);
  const [sensorWorking, setSensorWorking] = useState(null); // null=unknown, true/false
  const [installable, setInstallable] = useState(null);

  // Refs for the live-counting state so the motion listener doesn't reattach
  const lastStepAtRef = useRef(0);
  const sessionStepsRef = useRef(0);
  const sessionStartRef = useRef(null);
  const timerRef = useRef(null);
  const wakeLockRef = useRef(null); // screen-lock prevention during a walk
  const deviceId = useRef(getOrCreateDeviceId()).current;

  // Manual entry — for old phones / Fitbit users / anyone who just wants
  // to type today's total in.
  const [manualOpen, setManualOpen] = useState(false);
  const [manualSteps, setManualSteps] = useState("");
  const [manualSaving, setManualSaving] = useState(false);
  const [manualMsg, setManualMsg] = useState("");

  // Detect whether this device supports automatic step counting at all.
  // Old iPhones (iOS 12 and older) don't expose DeviceMotion permission API
  // AND don't fire accelerometer events from Safari. If neither is present
  // we hide the green button and lead with manual entry.
  const supportsAutomatic = typeof window !== "undefined" && "DeviceMotionEvent" in window;

  // Load today's totals + verse on mount
  useEffect(() => {
    api.get(`/walk/today/${deviceId}`).then((r) => setToday(r.data)).catch(() => {});
    api.get("/walk/verse").then((r) => setVerse(r.data)).catch(() => {});

    // Capture beforeinstallprompt so we can show our own "Put on phone" button
    const onInstall = (e) => { e.preventDefault(); setInstallable(e); };
    window.addEventListener("beforeinstallprompt", onInstall);
    return () => window.removeEventListener("beforeinstallprompt", onInstall);
  }, [deviceId]);

  // Tick session timer
  useEffect(() => {
    if (!walking) return undefined;
    sessionStartRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const secs = Math.floor((Date.now() - sessionStartRef.current) / 1000);
      setSessionSecs(secs);
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [walking]);

  const handleMotion = (e) => {
    // Total acceleration magnitude minus gravity. iOS gives both
    // accelerationIncludingGravity and acceleration (gravity-removed).
    const a = e.acceleration || e.accelerationIncludingGravity;
    if (!a) return;
    const x = a.x || 0, y = a.y || 0, z = a.z || 0;
    // Use a normalised "above-gravity" magnitude. accelerationIncludingGravity
    // rests at ~9.81; subtract that. acceleration rests at 0; use raw mag.
    let mag = Math.sqrt(x * x + y * y + z * z);
    if (e.accelerationIncludingGravity && !e.acceleration) {
      mag = Math.abs(mag - 9.81);
    }
    if (mag > STEP_THRESHOLD) {
      const now = Date.now();
      if (now - lastStepAtRef.current > STEP_COOLDOWN_MS) {
        lastStepAtRef.current = now;
        sessionStepsRef.current += 1;
        setSessionSteps(sessionStepsRef.current);
        setSensorWorking(true);
      }
    }
  };

  const startWalk = async () => {
    sessionStepsRef.current = 0;
    setSessionSteps(0);
    setSessionSecs(0);
    lastStepAtRef.current = 0;

    // iOS 13+ requires explicit permission for DeviceMotion
    const D = window.DeviceMotionEvent;
    if (D && typeof D.requestPermission === "function") {
      try {
        const res = await D.requestPermission();
        if (res !== "granted") {
          setNeedsPermission(true);
          return;
        }
      } catch (err) {
        setNeedsPermission(true);
        return;
      }
    }
    setNeedsPermission(false);
    window.addEventListener("devicemotion", handleMotion);
    setWalking(true);
    // Try to keep the screen awake while walking. Wake Lock API is supported
    // by Chrome on Android + Safari 16.4+ on iOS. Best-effort — if it fails
    // we simply fall back to our on-screen "KEEP THE SCREEN ON" reminder.
    try {
      if ("wakeLock" in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      }
    } catch (_e) { /* not fatal */ }
    // After 8 seconds with no motion, give a gentle hint
    setTimeout(() => {
      if (sessionStepsRef.current === 0) setSensorWorking(false);
    }, 8000);
  };

  const stopWalk = async () => {
    window.removeEventListener("devicemotion", handleMotion);
    setWalking(false);
    clearInterval(timerRef.current);
    // Release the wake-lock so the screen can sleep normally again
    try {
      if (wakeLockRef.current) {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    } catch (_e) { /* no-op */ }
    const steps = sessionStepsRef.current;
    const duration = Math.floor((Date.now() - (sessionStartRef.current || Date.now())) / 1000);
    if (steps > 0) {
      try {
        const r = await api.post("/walk/log", {
          device_id: deviceId,
          steps,
          duration_sec: duration,
        });
        setToday(r.data);
      } catch (err) { /* keep going */ }
    }
    // Hold session totals for a beat so the user sees what they just did,
    // then reset for the next walk
    setTimeout(() => {
      setSessionSteps(0);
      setSessionSecs(0);
    }, 6000);
  };

  /* Manual entry — anyone who tracked their walk on a Fitbit / Apple Watch /
     Garmin or just counted on their fingers can type their total here. We
     incrementally ADD to today's total so multiple manual saves accumulate. */
  const logManual = async () => {
    const n = parseInt((manualSteps || "").replace(/[^0-9]/g, ""), 10);
    if (!n || n < 1) { setManualMsg("Type a number of steps, like 1500."); return; }
    if (n > 200000) { setManualMsg("That's higher than the API allows — try a number under 200,000."); return; }
    setManualSaving(true);
    setManualMsg("");
    try {
      const r = await api.post("/walk/log", { device_id: deviceId, steps: n, duration_sec: 0 });
      setToday(r.data);
      setManualSteps("");
      setManualMsg(`★ Added ${n.toLocaleString()} steps to today's total.`);
    } catch (_err) {
      setManualMsg("Couldn't save right now — please try again in a moment.");
    } finally { setManualSaving(false); }
  };

  const onInstallTap = async () => {
    if (!installable) return;
    installable.prompt();
    try { await installable.userChoice; } catch (e) { /* no-op */ }
    setInstallable(null);
  };

  const goBack = () => {
    if (window.history.length > 1 && loc.key !== "default") return nav(-1);
    nav("/");
  };

  // Display helpers
  const miles = (today.steps / STEPS_PER_MILE).toFixed(2);
  const cals  = Math.round(today.steps * CAL_PER_STEP);
  const sessionMiles = (sessionSteps / STEPS_PER_MILE).toFixed(2);
  const sessionMin = Math.floor(sessionSecs / 60);
  const sessionRemSec = sessionSecs % 60;

  return (
    <div className="min-h-screen" style={{ background: "linear-gradient(180deg, #FFFFFF 0%, #E0F2FE 50%, #E0F2FE 100%)" }} data-testid="walk-page">
      {!embedded && <SiteNav />}

      {!embedded && (<button onClick={goBack} data-testid="walk-back"
              className="fixed top-20 left-3 md:left-6 z-30 inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs uppercase tracking-[0.22em] font-bold shadow-md hover:scale-[1.04] transition"
              style={{ background: "#FFFFFF", color: WINE, border: `2px solid ${O}` }}>
        <ArrowLeft className="w-4 h-4" /> Back
      </button>)}

      {/* HERO */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 pt-10 md:pt-14 pb-6 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] tracking-[0.28em] uppercase font-bold mb-3 text-white"
             style={{ background: G }}>
          <Footprints className="w-3.5 h-3.5" /> A FREE WALKING APP FROM MIKE
        </div>
        <h1 className="font-serif-display text-4xl sm:text-5xl md:text-6xl uppercase leading-[1.04]"
            style={{ color: WINE, textWrap: "balance" }}>
          Your Daily Walk.<br/>One Big Green Button.
        </h1>
      </section>

      {/* BIG NUMBER PANEL — today's total */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 pb-6">
        <div className="rounded-3xl bg-white shadow-xl p-7 md:p-10 text-center"
             style={{ border: `4px solid ${O}` }}
             data-testid="walk-today-panel">
          <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: O }}>
            ★ TODAY'S WALK
          </div>
          <div className="mt-3 font-serif-display font-bold tabular-nums"
               style={{ color: WINE, fontSize: "clamp(72px, 14vw, 140px)", lineHeight: 1 }}
               data-testid="walk-today-steps">
            {today.steps.toLocaleString()}
          </div>
          <div className="text-xl md:text-2xl uppercase tracking-[0.18em] font-bold mt-1" style={{ color: WINE }}>
            Steps
          </div>

          <div className="grid grid-cols-3 gap-2 md:gap-4 mt-7">
            <Stat label="Miles" value={miles} />
            <Stat label="Calories" value={cals.toLocaleString()} />
            <Stat label={<span><Flame className="inline w-4 h-4 mb-0.5" /> Streak</span>}
                  value={`${today.streak_days || 0}d`} testid="walk-streak" />
          </div>
        </div>
      </section>

      {/* THE ONE GIANT BUTTON — only on phones that can auto-count */}
      {supportsAutomatic && (
      <section className="max-w-3xl mx-auto px-5 md:px-8 pb-8">
        {!walking ? (
          <button onClick={startWalk}
                  data-testid="walk-start"
                  className="w-full inline-flex items-center justify-center gap-3 py-7 md:py-9 rounded-3xl text-2xl md:text-4xl font-bold uppercase tracking-[0.18em] text-white shadow-2xl hover:scale-[1.02] active:scale-[0.99] transition"
                  style={{ background: G }}>
            <Play className="w-9 h-9 md:w-12 md:h-12" fill="#fff" /> Start Walking
          </button>
        ) : (
          <div className="space-y-4" data-testid="walk-active">
            <div className="rounded-3xl shadow-xl p-7 md:p-9 text-center"
                 style={{ background: G, border: `4px solid ${O}` }}>
              <div className="text-[10px] tracking-[0.32em] uppercase font-bold text-white/90">
                ★ WALK IN PROGRESS ★ KEEP THE SCREEN ON
              </div>
              <div className="mt-3 font-serif-display font-bold tabular-nums text-white"
                   style={{ fontSize: "clamp(64px, 12vw, 120px)", lineHeight: 1 }}
                   data-testid="walk-session-steps">
                {sessionSteps.toLocaleString()}
              </div>
              <div className="text-lg md:text-xl uppercase tracking-[0.18em] font-bold text-white">
                steps this walk
              </div>
              <div className="text-sm md:text-base mt-2 text-white/90 tabular-nums">
                {sessionMin}:{String(sessionRemSec).padStart(2, "0")} · {sessionMiles} mi
              </div>
            </div>
            <button onClick={stopWalk}
                    data-testid="walk-stop"
                    className="w-full inline-flex items-center justify-center gap-3 py-6 md:py-8 rounded-3xl text-xl md:text-3xl font-bold uppercase tracking-[0.18em] text-white shadow-xl hover:scale-[1.02] active:scale-[0.99] transition"
                    style={{ background: "#B71C1C" }}>
              <Square className="w-7 h-7 md:w-9 md:h-9" fill="#fff" /> I&apos;m Done
            </button>
            {sensorWorking === false && (
              <div className="rounded-xl p-4 bg-white text-center text-sm" style={{ border: `2px dashed ${O}` }}>
                <strong>Not picking up your steps yet.</strong> Keep the phone in your hand or pocket and start walking — it&apos;ll catch on.
              </div>
            )}
          </div>
        )}
      </section>
      )}

      {/* iOS PERMISSION FALLBACK */}
      {needsPermission && (
        <section className="max-w-3xl mx-auto px-5 md:px-8 pb-4">
          <div className="rounded-xl p-5 text-center" style={{ background: "#FFF", border: `3px solid ${O}` }}>
            <div className="font-bold uppercase tracking-[0.18em] text-sm mb-2" style={{ color: WINE }}>
              ★ ALMOST THERE
            </div>
            <p className="text-base" style={{ color: WINE }}>
              Your iPhone is asking for permission to use the motion sensor. Tap <strong>Allow</strong> when it asks, then tap the green button again.
            </p>
          </div>
        </section>
      )}

      {/* MANUAL ENTRY — for old phones, Fitbit users, anyone who just wants
          to type the number. Collapsed by default so it doesn't crowd the
          green button. Auto-expanded on phones with no DeviceMotion at all. */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 pb-6" data-testid="walk-manual">
        {(!manualOpen && supportsAutomatic) && (
          <button onClick={() => setManualOpen(true)}
                  data-testid="walk-manual-toggle"
                  className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-xl text-sm font-bold uppercase tracking-[0.18em] bg-white hover:bg-white transition"
                  style={{ color: WINE, border: `2px solid ${O}55` }}>
            Already Tracked On A Fitbit / Apple Watch? Type My Steps →
          </button>
        )}
        {(manualOpen || !supportsAutomatic) && (
          <div className="rounded-2xl bg-white shadow-md p-5 md:p-6" style={{ border: `3px solid ${G}` }}>
            <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-1" style={{ color: G }}>
              ★ TYPE TODAY'S TOTAL
            </div>
            <h3 className="font-serif-display text-xl md:text-2xl uppercase leading-tight"
                style={{ color: WINE, textWrap: "balance" }}>
              {!supportsAutomatic
                ? "Your Phone Can't Auto-Count — Type It In Below."
                : "Have A Fitbit Or Watch? Just Type Your Steps."}
            </h3>
            <p className="text-sm mt-2" style={{ color: "#0F172A" }}>
              Look at your watch / Fitbit / phone health app. Type today&apos;s step total.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <input
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                value={manualSteps}
                onChange={(e) => setManualSteps(e.target.value)}
                placeholder="e.g. 4250"
                autoComplete="off"
                data-testid="walk-manual-input"
                className="flex-1 min-w-[160px] px-4 py-3 rounded-md text-2xl font-bold tabular-nums outline-none"
                style={{ border: `2px solid ${O}55`, color: WINE, background: "#FFFFFF" }}
              />
              <button
                type="button"
                onClick={logManual}
                disabled={manualSaving}
                data-testid="walk-manual-save"
                className="inline-flex items-center gap-2 px-7 py-3 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow hover:scale-[1.03] transition disabled:opacity-60"
                style={{ background: G }}>
                {manualSaving ? "Saving…" : "Add To Today"}
              </button>
            </div>
            {manualMsg && (
              <div className="mt-3 text-sm font-bold" style={{ color: manualMsg.startsWith("★") ? G : WINE }}>
                {manualMsg}
              </div>
            )}
            {manualOpen && supportsAutomatic && (
              <button onClick={() => { setManualOpen(false); setManualMsg(""); }}
                      className="mt-3 text-xs uppercase tracking-[0.18em] font-bold hover:opacity-70"
                      style={{ color: WINE }}>
                ← Hide This
              </button>
            )}
          </div>
        )}
      </section>

      {/* DAILY VERSE */}
      {verse && (
        <section className="max-w-3xl mx-auto px-5 md:px-8 py-8" data-testid="walk-verse">
          <div className="rounded-2xl p-6 md:p-8 text-center bg-white shadow"
               style={{ border: `2px solid ${O}55` }}>
            <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: O }}>
              ★ TODAY&apos;S VERSE
            </div>
            <p className="font-serif-display italic mt-3"
               style={{ color: WINE, fontSize: "clamp(22px, 3.6vw, 32px)", lineHeight: 1.35 }}>
              &ldquo;{verse.text}&rdquo;
            </p>
            <div className="mt-3 text-sm md:text-base uppercase tracking-[0.22em] font-bold"
                 style={{ color: O }}>
              — {verse.ref}
            </div>
          </div>
        </section>
      )}

      {/* FAQ — short, written like Mike explaining to a friend */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 pb-8" data-testid="walk-faq">
        <details className="rounded-xl bg-white p-5" style={{ border: `2px solid ${O}55` }}>
          <summary className="cursor-pointer font-bold uppercase tracking-[0.18em] text-sm md:text-base"
                   style={{ color: WINE }}>
            ★ Why aren&apos;t my steps counting? (tap to read)
          </summary>
          <div className="mt-4 space-y-3 text-base leading-relaxed" style={{ color: "#0F172A" }}>
            <p><strong>★ Keep the screen on.</strong> iPhones stop the motion sensor when the screen sleeps. We try to keep it awake during your walk, but turn off &ldquo;Auto-Lock&rdquo; in Settings → Display if you carry the phone in a pocket.</p>
            <p><strong>★ Hold the phone in your hand, or use a pocket.</strong> The sensor needs your body&apos;s natural up-down motion. Lying on a table = no counts.</p>
            <p><strong>★ Older iPhone (before 2019)?</strong> Your phone can&apos;t auto-count. Use the <strong>green &ldquo;TYPE TODAY&apos;S TOTAL&rdquo;</strong> box above instead — read the number off your watch or fitness band and type it in.</p>
            <p><strong>★ The count is close, not perfect.</strong> Like every phone-based pedometer (Fitbit included), expect to be within ~10%. Good enough for a daily walking habit.</p>
          </div>
        </details>
      </section>

      {/* "Put this on my phone" guide */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 pb-16">
        <div className="rounded-2xl bg-white p-6 md:p-8 shadow-md" style={{ border: `3px solid ${G}` }}>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0"
                 style={{ background: G }}>
              <Smartphone className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: G }}>
                ★ ONE-TIME SETUP
              </div>
              <h3 className="font-serif-display text-2xl md:text-3xl uppercase leading-tight mt-1"
                  style={{ color: WINE, textWrap: "balance" }}>
                Put This On Your Phone&apos;s Home Screen.
              </h3>
              <p className="mt-3 text-base md:text-lg leading-relaxed" style={{ color: "#0F172A" }}>
                Saves like a real app icon. No app-store download needed.
              </p>
              {installable ? (
                <button onClick={onInstallTap}
                        data-testid="walk-install"
                        className="mt-4 inline-flex items-center gap-2 px-7 py-4 rounded-md text-base font-bold uppercase tracking-[0.18em] text-white shadow hover:scale-[1.03] transition"
                        style={{ background: G }}>
                  <Smartphone className="w-5 h-5" /> Put It On My Phone
                </button>
              ) : (
                <div className="mt-4 text-base leading-relaxed" style={{ color: "#0F172A" }}>
                  <strong>On iPhone (Safari):</strong> tap the <span style={{ background: "#D9EEFB", padding: "2px 8px", borderRadius: 6 }}>Share</span> button at the bottom → scroll down → tap <strong>Add to Home Screen</strong>.<br/><br/>
                  <strong>On Android (Chrome):</strong> tap the <span style={{ background: "#D9EEFB", padding: "2px 8px", borderRadius: 6 }}>⋮</span> menu top-right → tap <strong>Add to Home screen</strong>.
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {!embedded && <SiteFooter />}
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl px-3 py-3 md:py-4" style={{ background: "#EAF6FC", border: `2px solid ${O}33` }} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold mb-1" style={{ color: WINE }}>{label}</div>
      <div className="font-serif-display font-bold tabular-nums" style={{ color: WINE, fontSize: "clamp(24px, 4vw, 36px)", lineHeight: 1 }}>
        {value}
      </div>
    </div>
  );
}
